import {
  ASSET_STATUSES,
  ASSET_TYPES,
  type AssetDto,
  type AssetStatus,
  type AssetType,
  updateAssetInput,
} from '@fos/shared/contracts/maintenance';
import { ASSET_STATUS_LABELS, ASSET_TYPE_LABELS } from '@fos/shared/labels';
import { Archive } from 'lucide-react';
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { useFocusFirstInvalid } from '../../../lib/useFocusInvalid';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Input, Textarea } from '../../../ui/Input';
import { Select, type SelectOption } from '../../../ui/Select';
import { toast } from '../../../ui/Toast';
import { FULLSCREEN_ON_PHONE } from '../../mobile';
import { issuesToErrors, zodIssues } from '../form-utils';

const NONE = '__none__';
const FIELD_KEYS = [
  'type',
  'name',
  'address',
  'link',
  'provider',
  'server',
  'tech',
  'repoUrl',
  'status',
  'notesMd',
] as const;
type FieldKey = (typeof FIELD_KEYS)[number];

interface FormState {
  type: AssetType;
  name: string;
  address: string;
  link: string;
  provider: string;
  server: string;
  tech: string;
  repoUrl: string;
  status: AssetStatus;
  notesMd: string;
}

function initial(a: AssetDto): FormState {
  return {
    type: a.type,
    name: a.name,
    address: a.address ?? '',
    link: a.serviceId ? `service:${a.serviceId}` : a.projectId ? `project:${a.projectId}` : NONE,
    provider: a.provider ?? '',
    server: a.server ?? '',
    tech: a.tech ?? '',
    repoUrl: a.repoUrl ?? '',
    status: a.status,
    notesMd: a.notesMd ?? '',
  };
}

const orNull = (v: string) => (v.trim() ? v.trim() : null);

/**
 * Edycja zasobu (karta zasobu → „Edytuj”; widok bez osobnego ekranu w Figmie — wzorzec modalu „Dodaj zasób”):
 * typ, nazwa, adres, projekt albo umowa, gdzie stoi, technologia, repozytorium, status, notatki; archiwizacja.
 * Klienta zasobu nie zmieniamy tutaj (zasób z umową należy do klienta umowy).
 */
export function EditAssetDialog({
  open,
  onOpenChange,
  asset,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  asset: AssetDto;
}) {
  const utils = trpc.useUtils();
  const options = trpc.maintenance.formOptions.useQuery(undefined, { enabled: open });
  const update = trpc.maintenance.updateAsset.useMutation();
  const archive = trpc.maintenance.archiveAsset.useMutation();
  const [form, setForm] = useState<FormState>(() => initial(asset));
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, errors);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset tylko przy otwarciu
  useEffect(() => {
    if (!open) return;
    setForm(initial(asset));
    setErrors({});
    setFormError(null);
  }, [open]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));
  const client = options.data?.clients.find((c) => c.id === asset.client.id);
  const linkOptions = useMemo<SelectOption[]>(() => {
    const list: SelectOption[] = [{ value: NONE, label: 'Bez projektu i umowy' }];
    for (const p of client?.projects ?? []) list.push({ value: `project:${p.id}`, label: `Projekt: ${p.name}` });
    for (const s of client?.services ?? []) list.push({ value: `service:${s.id}`, label: `Umowa: ${s.name}` });
    if (form.link !== NONE && !list.some((o) => o.value === form.link)) {
      list.push({
        value: form.link,
        label: form.link.startsWith('project:') ? `Projekt: ${asset.projectName ?? '—'}` : 'Umowa klienta',
      });
    }
    return list;
  }, [client, form.link, asset.projectName]);

  const done = async (message: string) => {
    toast.success(message);
    await utils.maintenance.invalidate();
    onOpenChange(false);
  };

  const fail = (err: unknown) => {
    if (appErrorCode(err) === 'VALIDATION_ERROR') {
      const fieldErrors = issuesToErrors(validationIssues(err), FIELD_KEYS);
      if (Object.keys(fieldErrors).length > 0) {
        setErrors(fieldErrors);
        return;
      }
    }
    setFormError(
      appErrorCode(err) === 'CONFLICT' ? 'Ktoś zmienił ten zasób. Zamknij okno i spróbuj ponownie.' : errorMessage(err),
    );
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const input = {
      id: asset.id,
      version: asset.version,
      type: form.type,
      name: form.name,
      address: orNull(form.address),
      projectId: form.link.startsWith('project:') ? form.link.slice(8) : null,
      serviceId: form.link.startsWith('service:') ? form.link.slice(8) : null,
      provider: orNull(form.provider),
      server: orNull(form.server),
      tech: orNull(form.tech),
      repoUrl: orNull(form.repoUrl),
      status: form.status,
      notesMd: orNull(form.notesMd),
    };
    const parsed = updateAssetInput.safeParse(input);
    if (!parsed.success) {
      setErrors(issuesToErrors(zodIssues(parsed.error), FIELD_KEYS));
      return;
    }
    try {
      await update.mutateAsync(input);
      await done('Zapisano zasób');
    } catch (err) {
      fail(err);
    }
  };

  const toggleArchive = async () => {
    setFormError(null);
    try {
      await archive.mutateAsync({ id: asset.id, version: asset.version, archived: !asset.archivedAt });
      await done(asset.archivedAt ? 'Przywrócono zasób' : 'Zasób w archiwum');
    } catch (err) {
      fail(err);
    }
  };

  const pending = update.isPending || archive.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="l"
      title="Edytuj zasób"
      className={FULLSCREEN_ON_PHONE}
      footer={
        <>
          <Button variant="ghost" icon={Archive} onClick={toggleArchive} disabled={pending} className="mr-auto">
            {asset.archivedAt ? 'Przywróć z archiwum' : 'Archiwizuj'}
          </Button>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" form="edit-asset-form" loading={update.isPending}>
            Zapisz
          </Button>
        </>
      }
    >
      <form id="edit-asset-form" ref={formRef} onSubmit={submit} noValidate className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Typ" error={errors.type}>
            <Select
              value={form.type}
              onValueChange={(v) => set('type', v as AssetType)}
              options={ASSET_TYPES.map((t) => ({ value: t, label: ASSET_TYPE_LABELS[t] }))}
            />
          </Field>
          <Field label="Status" error={errors.status}>
            <Select
              value={form.status}
              onValueChange={(v) => set('status', v as AssetStatus)}
              options={ASSET_STATUSES.map((s) => ({ value: s, label: ASSET_STATUS_LABELS[s] }))}
            />
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Nazwa" error={errors.name}>
            <Input value={form.name} onChange={(e) => set('name', e.target.value)} />
          </Field>
          <Field label="Adres" error={errors.address}>
            <Input value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="https://…" />
          </Field>
        </div>
        <Field label="Projekt lub usługa" error={errors.link}>
          <Select value={form.link} onValueChange={(v) => set('link', v)} options={linkOptions} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Dostawca" error={errors.provider}>
            <Input value={form.provider} onChange={(e) => set('provider', e.target.value)} placeholder="np. Zenbox" />
          </Field>
          <Field label="Serwer" error={errors.server}>
            <Input value={form.server} onChange={(e) => set('server', e.target.value)} placeholder="np. s55" />
          </Field>
          <Field label="Technologia" error={errors.tech}>
            <Input value={form.tech} onChange={(e) => set('tech', e.target.value)} placeholder="np. WordPress" />
          </Field>
        </div>
        <Field label="Repozytorium" error={errors.repoUrl}>
          <Input
            type="url"
            value={form.repoUrl}
            onChange={(e) => set('repoUrl', e.target.value)}
            placeholder="https://github.com/…"
          />
        </Field>
        <Field label="Notatki" error={errors.notesMd} hint="Możesz użyć Markdown.">
          <Textarea rows={3} value={form.notesMd} onChange={(e) => set('notesMd', e.target.value)} />
        </Field>
        {formError && <FormMessage>{formError}</FormMessage>}
      </form>
    </Dialog>
  );
}
